import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 16
const NAME_START = 0x0d00
const WIDE_DCS = [...DTCS_CODES, 645].sort((left, right) => left - right)
const STANDARD_RANGES = [[0x0000, 0x0b60]] as const
const NAMED_RANGES = [
  [0x0000, 0x0640],
  [0x0d00, 0x1040],
] as const
const FRS_RANGES = [
  [0x0000, 0x0640],
  [0x0d00, 0x0dc0],
] as const

type BFT8Spec = {
  id: string
  vendor: string
  model: string
  summary: string
  magic: readonly number[]
  fingerprint: readonly number[]
  channels: number
  memorySize: number
  nameLength: 0 | 6
  wideDcs?: boolean
  ranges: readonly (readonly [number, number])[]
}

export type BFT8Profile = {
  protocol: 'bft8'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 0 | 6
  memorySize: number
  magic: readonly number[]
  fingerprint: readonly number[]
  wideDcs: boolean
  ranges: readonly (readonly [number, number])[]
  powerLevels: readonly ['High', 'Low']
}

function ascii(value: string): readonly number[] {
  return Array.from(value, (character) => character.charCodeAt(0) & 0xff)
}

const MAGIC = [0x02, ...ascii('PROGRAM')]
const T8_FINGERPRINT = ascii('.BF-T6.')
const FRS_A1_FINGERPRINT = ascii('BF-T8A.')

const SPECS: readonly BFT8Spec[] = [
  {
    id: 'baofeng-bf-t8',
    vendor: 'Baofeng',
    model: 'BF-T8',
    summary: 'Baofeng BF-T8 compact 99-channel radio.',
    magic: MAGIC,
    fingerprint: T8_FINGERPRINT,
    channels: 99,
    memorySize: 0x0b60,
    nameLength: 0,
    ranges: STANDARD_RANGES,
  },
  {
    id: 'baofeng-bf-u9',
    vendor: 'Baofeng',
    model: 'BF-U9',
    summary: 'Baofeng BF-U9 compact BF-T8-compatible radio.',
    magic: MAGIC,
    fingerprint: T8_FINGERPRINT,
    channels: 99,
    memorySize: 0x0b60,
    nameLength: 0,
    ranges: STANDARD_RANGES,
  },
  {
    id: 'arcshell-ar-8',
    vendor: 'Arcshell',
    model: 'AR-8',
    summary: 'Arcshell AR-8 compact BF-T8-compatible radio.',
    magic: MAGIC,
    fingerprint: T8_FINGERPRINT,
    channels: 99,
    memorySize: 0x0b60,
    nameLength: 0,
    ranges: STANDARD_RANGES,
  },
  {
    id: 'retevis-rt16',
    vendor: 'Retevis',
    model: 'RT16',
    summary: 'Retevis RT16 compact FRS radio.',
    magic: MAGIC,
    fingerprint: T8_FINGERPRINT,
    channels: 22,
    memorySize: 0x0b60,
    nameLength: 0,
    ranges: STANDARD_RANGES,
  },
  {
    id: 'retevis-rb27b',
    vendor: 'Retevis',
    model: 'RB27B',
    summary: 'Retevis RB27B compact FRS radio with channel names.',
    magic: MAGIC,
    fingerprint: T8_FINGERPRINT,
    channels: 22,
    memorySize: 0x1040,
    nameLength: 6,
    wideDcs: true,
    ranges: NAMED_RANGES,
  },
  {
    id: 'retevis-rb27',
    vendor: 'Retevis',
    model: 'RB27',
    summary: 'Retevis RB27 compact 99-channel radio with channel names.',
    magic: MAGIC,
    fingerprint: T8_FINGERPRINT,
    channels: 99,
    memorySize: 0x1040,
    nameLength: 6,
    wideDcs: true,
    ranges: NAMED_RANGES,
  },
  {
    id: 'retevis-rb27v',
    vendor: 'Retevis',
    model: 'RB27V',
    summary: 'Retevis RB27V compact MURS radio.',
    magic: MAGIC,
    fingerprint: T8_FINGERPRINT,
    channels: 5,
    memorySize: 0x1040,
    nameLength: 6,
    wideDcs: true,
    ranges: NAMED_RANGES,
  },
  {
    id: 'retevis-rb627b',
    vendor: 'Retevis',
    model: 'RB627B',
    summary: 'Retevis RB627B compact PMR radio.',
    magic: MAGIC,
    fingerprint: T8_FINGERPRINT,
    channels: 16,
    memorySize: 0x1040,
    nameLength: 6,
    wideDcs: true,
    ranges: NAMED_RANGES,
  },
  {
    id: 'btech-frs-a1',
    vendor: 'BTECH',
    model: 'FRS-A1',
    summary: 'BTECH FRS-A1 compact 22-channel radio.',
    magic: MAGIC,
    fingerprint: FRS_A1_FINGERPRINT,
    channels: 22,
    memorySize: 0x0dc0,
    nameLength: 6,
    wideDcs: true,
    ranges: FRS_RANGES,
  },
]

export const BFT8_PROFILES: readonly BFT8Profile[] = SPECS.map((spec) => ({
  protocol: 'bft8',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: [
    'Experimental',
    'Read + write',
    `${spec.channels} channels`,
    spec.nameLength ? '6-character names' : 'No channel names',
  ],
  baudRate: 9600,
  channelMinimum: 1,
  channelMaximum: spec.channels,
  nameLength: spec.nameLength,
  memorySize: spec.memorySize,
  magic: spec.magic,
  fingerprint: spec.fingerprint,
  wideDcs: spec.wideDcs ?? false,
  ranges: spec.ranges,
  powerLevels: ['High', 'Low'],
}))

function dcsCodes(profile: BFT8Profile): readonly number[] {
  return profile.wideDcs ? WIDE_DCS : DTCS_CODES
}

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'High',
    skip: false,
  }
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(
  mode: number,
  value: number,
  profile: BFT8Profile,
): DecodedTone {
  if (mode === 1) {
    return {
      mode: 'ctcss',
      ctcss: CTCSS_TONES[value] ?? null,
      dtcs: null,
      polarity: 'N',
    }
  }
  if (mode === 2 || mode === 3) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: dcsCodes(profile)[value] ?? null,
      polarity: mode === 3 ? 'R' : 'N',
    }
  }
  return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
}

function encodeTone(
  channel: WebChannel,
  profile: BFT8Profile,
): readonly [number, number] {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const index = CTCSS_TONES.indexOf(
      channel.ctcss as (typeof CTCSS_TONES)[number],
    )
    if (index < 0) throw new Error('That CTCSS tone is not supported by this radio.')
    return [1, index]
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = dcsCodes(profile).indexOf(channel.dtcs)
    if (index < 0) throw new Error('That DCS code is not supported by this radio.')
    return [channel.dtcsPolarity === 'R' ? 3 : 2, index]
  }
  return [0, 0]
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') {
    return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  }
  return (
    tone.mode === 'dtcs' &&
    tone.dtcs === channel.dtcs &&
    tone.polarity === channel.dtcsPolarity
  )
}

function nameOffset(number: number): number {
  return NAME_START + (number - 1) * 8
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value === 0xff ? 0x20 : value)),
  )
    .replace(/\0/g, ' ')
    .trim()
}

export function parseBFT8Channel(
  image: Uint8Array,
  profile: BFT8Profile,
  number: number,
): WebChannel {
  const offset = (number - 1) * BLOCK_SIZE
  const raw = image.slice(offset, offset + BLOCK_SIZE)
  if (raw.byteLength !== BLOCK_SIZE || raw[0] === 0xff) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const txFrequency =
    raw[4] === 0xff ? null : decodeLbcd(raw.slice(4, 8))
  const txTone = decodeTone(raw[10], raw[11], profile)
  const rxtone = decodeTone(raw[8], raw[9], profile)
  const selected = txTone.mode === 'none' ? rxtone : txTone
  return {
    number,
    empty: false,
    name: profile.nameLength
      ? decodeName(
          image.slice(nameOffset(number), nameOffset(number) + profile.nameLength),
        )
      : '',
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[12] & 0x40 ? 'NFM' : 'FM',
    power: raw[12] & 0x80 ? 'Low' : 'High',
    skip: (raw[12] & 0x08) !== 0,
  }
}

export function parseBFT8Channels(
  image: Uint8Array,
  profile: BFT8Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseBFT8Channel(image, profile, index + 1),
  )
}

export function applyBFT8Channel(
  image: Uint8Array,
  profile: BFT8Profile,
  channel: WebChannel,
): void {
  if (channel.number < 1 || channel.number > profile.channelMaximum) {
    throw new Error(`Channel must be between 1 and ${profile.channelMaximum}.`)
  }
  const offset = (channel.number - 1) * BLOCK_SIZE
  const raw = image.subarray(offset, offset + BLOCK_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    if (profile.nameLength) {
      image.fill(0xff, nameOffset(channel.number), nameOffset(channel.number) + 8)
    }
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw[0] === 0xff) {
    raw.fill(0)
    raw.fill(0xff, 13)
  }
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  const existingTx = decodeTone(raw[10], raw[11], profile)
  const existingRx = decodeTone(raw[8], raw[9], profile)
  if (
    !toneMatches(channel, existingTx) ||
    (!toneMatches(channel, existingRx) && existingRx.mode !== 'none')
  ) {
    const [txMode, txValue] = encodeTone(channel, profile)
    const [rxMode, rxValue] = encodeTone(channel, profile)
    raw[8] = rxMode
    raw[9] = rxValue
    raw[10] = txMode
    raw[11] = txValue
  }
  raw[12] =
    (raw[12] & 0x30) |
    (channel.skip ? 0x08 : 0) |
    (channel.mode === 'NFM' || channel.mode === 'AM' ? 0x40 : 0) |
    (channel.power === 'Low' ? 0x80 : 0)
  if (profile.nameLength) {
    const nameOffsetValue = nameOffset(channel.number)
    image.fill(0xff, nameOffsetValue, nameOffsetValue + 8)
    for (let index = 0; index < Math.min(profile.nameLength, channel.name.length); index += 1) {
      image[nameOffsetValue + index] = channel.name.charCodeAt(index) & 0xff
    }
  }
}

async function readAck(
  transport: RadioTransport,
  message: string,
): Promise<void> {
  if ((await transport.readExact(1, 3000))[0] !== ACK) throw new Error(message)
}

async function enterProgrammingMode(
  profile: BFT8Profile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  await transport.write(Uint8Array.from(profile.magic))
  await readAck(transport, 'The radio refused BF-T8 programming mode.')
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(profile.fingerprint.length, 3000)
  if (
    !profile.fingerprint.every((value, index) => identity[index] === value)
  ) {
    throw new Error(
      `The connected radio does not identify as ${profile.vendor} ${profile.model}.`,
    )
  }
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport, 'The radio rejected the BF-T8 clone session.')
}

async function exitProgrammingMode(transport: RadioTransport): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  await transport.write(
    Uint8Array.of(0x52, (address >> 8) & 0xff, address & 0xff, BLOCK_SIZE),
  )
  const response = await transport.readExact(4 + BLOCK_SIZE, 3000)
  if (
    response[0] !== 0x57 ||
    response[1] !== ((address >> 8) & 0xff) ||
    response[2] !== (address & 0xff) ||
    response[3] !== BLOCK_SIZE
  ) {
    throw new Error(`Unexpected BF-T8 read reply at 0x${address.toString(16)}.`)
  }
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport, `The radio rejected read block 0x${address.toString(16)}.`)
  return response.slice(4)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const frame = new Uint8Array(4 + BLOCK_SIZE)
  frame.set([
    0x57,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  ])
  frame.set(data, 4)
  await transport.write(frame)
  await readAck(transport, `The radio rejected write block 0x${address.toString(16)}.`)
}

export async function readBFT8RadioWithTransport(
  profile: BFT8Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(profile.memorySize)
    const total = profile.memorySize / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading BF-T8-family memory',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export async function writeBFT8RadioWithTransport(
  profile: BFT8Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than this radio memory map.')
  }
  await enterProgrammingMode(profile, transport)
  try {
    const addresses = profile.ranges.flatMap(([start, end]) =>
      Array.from(
        { length: (end - start) / BLOCK_SIZE },
        (_, index) => start + index * BLOCK_SIZE,
      ),
    )
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      await writeBlock(
        transport,
        address,
        image.slice(address, address + BLOCK_SIZE),
      )
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Writing BF-T8-family memory',
      })
    }
  } finally {
    await exitProgrammingMode(transport)
  }
}
